import { AnyAction, Reducer } from "redux";
import { createPatch } from "rfc6902";
import { IAppThunkAction } from ".";
import FileApi from "../api/FileApi";
import ParcelApi from "../api/ParcelApi";
import IErrorInfo from "../models/account/IErrorInfo";
import { IAuctionInfo } from "../models/auction/IAuctionInfo";
import IAuctionStatus, {
    AuctionStatus
} from "../models/auction/IAuctionStatus";
import { FileType } from "../models/files/FileType";
import IFileMetadata from "../models/files/IFileMetadata";
import { IBidder } from "../models/parcel/IBidder";
import ICreateUpdateParcel from "../models/parcel/ICreateUpdateParcel";
import { IParcelDetail } from "../models/parcel/IParcelDetail";
import { ISortParcel } from "../models/parcel/ISortParcel";
import { LoadState } from "../models/utility/LoadState";
import * as ParcelBidStore from "./ParcelBidStore";

export interface IParcelStoreState {
    auctionInfo: IAuctionInfo;
    parcelUpdateMessage: string;
    parcelsLoadState: LoadState;
    parcels: IParcelDetail[];
    savingNewParcel: boolean;
    savingNewParcelErrors: IErrorInfo[];
    savingParcelOrder: boolean;
    savingParcelOrderErrors: IErrorInfo[];
    showParcelUpdateMessage: boolean;
}

export type KnownAction =
    | IArchiveParcel
    | IDeleteParcel
    | IHideParcelUpdatedSnackbarMessage
    | IShowParcelUpdatedSnackbarMessage
    | IToggleAuctionLiveStatus
    | IToggleMetadataCustomLoadState
    | IToggleMetadataLandTitleLoadState
    | IToggleMetadataParcelPictureLoadState
    | IToggleMetadataPurcAgmtLoadState
    | IToggleParcelCompletedStatus
    | IToggleParcelLoadState
    | IToggleSavingNewParcel
    | IToggleSavingParcel
    | IToggleUpdatingParcelOrder
    | IUpdateParcel
    | IUpdateMetadataCustom
    | IUpdateMetadataLandTitle
    | IUpdateMetadataParcelPicture
    | IUpdateMetadataPurcAgmt
    | IUpdateParcelBidAmount
    | IUpdateLiveParcelInformation
    | IUpdateParcelList
    | IUpdateParcelBidders
    | ICheckpointLiveParcels;

export interface IArchiveParcel {
    type: "ARCHIVE_PARCEL";
    parcelId: string;
    isArchiving: boolean;
}
export interface IDeleteParcel {
    type: "DELETE_PARCEL";
    parcelId: string;
}
export interface IHideParcelUpdatedSnackbarMessage {
    type: "HIDE_PARCEL_UPDATED_SNACKBAR_MESSAGE";
}
export interface IShowParcelUpdatedSnackbarMessage {
    type: "SHOW_PARCEL_UPDATED_SNACKBAR_MESSAGE";
    auctionId: string;
    message: string;
}
export interface IToggleAuctionLiveStatus {
    type: "TOGGLE_AUCTION_LIVE_STATUS";
    auctionId: string;
    live: boolean;
}
export interface IToggleMetadataCustomLoadState {
    type: "TOGGLE_METADATA_CUSTOM_LOAD_STATE";
    parcelId: string;
    loadState: LoadState;
}
export interface IToggleMetadataLandTitleLoadState {
    type: "TOGGLE_METADATA_LAND_TITLE_LOAD_STATE";
    parcelId: string;
    loadState: LoadState;
}
export interface IToggleMetadataParcelPictureLoadState {
    type: "TOGGLE_METADATA_PARCEL_PICTURE_LOAD_STATE";
    parcelId: string;
    loadState: LoadState;
}
export interface IToggleMetadataPurcAgmtLoadState {
    type: "TOGGLE_METADATA_PURC_AGMT_LOAD_STATE";
    parcelId: string;
    loadState: LoadState;
}
export interface IToggleParcelCompletedStatus {
    type: "TOGGLE_PARCEL_LIVE_STATUS";
    parcelId: string;
    startDate: string;
    endDate: string;
    isCompleted: boolean;
    isLive: boolean;
}
export interface IToggleParcelLoadState {
    type: "TOGGLE_PARCEL_LOAD_STATE";
    parcelsLoadState: LoadState;
}
export interface IToggleSavingNewParcel {
    type: "TOGGLE_SAVING_NEW_PARCEL";
    savingNewParcel: boolean;
    savingNewParcelErrors: IErrorInfo[];
}
export interface IToggleSavingParcel {
    type: "TOGGLE_SAVING_PARCEL";
    isSaving: boolean;
    parcelId: string;
    savingErrors: IErrorInfo[];
}
export interface IToggleUpdatingParcelOrder {
    type: "TOGGLE_UPDATING_PARCEL_ORDER";
    isSaving: boolean;
    updatingErrors: IErrorInfo[];
}
export interface IUpdateMetadataCustom {
    type: "UPDATE_METADATA_CUSTOM";
    parcelId: string;
    customMetadata: IFileMetadata[];
}
export interface IUpdateMetadataLandTitle {
    type: "UPDATE_METADATA_LAND_TITLE";
    parcelId: string;
    landTitleMetadata: IFileMetadata[];
}
export interface IUpdateMetadataParcelPicture {
    type: "UPDATE_METADATA_PARCEL_PICTURE";
    parcelId: string;
    pictureMetadata: IFileMetadata[] | null;
    thumbMetadata: IFileMetadata[] | null;
}
export interface IUpdateMetadataPurcAgmt {
    type: "UPDATE_METADATA_PURC_AGMT";
    parcelId: string;
    purchaseAgmtMetadata: IFileMetadata[];
}
export interface IUpdateParcel {
    type: "UPDATE_PARCEL";
    parcelId: string;
    updatedParcel: ICreateUpdateParcel;
}
export interface IUpdateLiveParcelInformation {
    type: "UPDATE_LIVE_PARCEL_INFORMATION";
    auctionStatus: IAuctionStatus;
}
export interface IUpdateParcelBidAmount {
    type: "UPDATE_PARCEL_BID_AMOUNT";
    amount: number;
    parcelId: string;
    tokenId: string;
    endDate: string;
}
export interface IUpdateParcelList {
    type: "UPDATE_PARCEL_LIST";
    auctionInfo: IAuctionInfo;
    parcels: IParcelDetail[];
    parcelsLoadState: LoadState;
}
export interface IUpdateParcelBidders {
    type: "UPDATE_PARCEL_BIDDERS";
    parcelId: string;
    approvedEnBlocBidders: IBidder[];
}
export interface ICheckpointLiveParcels {
    type: "CHECKPOINT_LIVE_PARCELS";
    liveParcels: IAuctionStatus[];
    resetConnection: () => void;
}

export const actionCreators = {
    createParcel:
        (url: string, newParcel: ICreateUpdateParcel): IAppThunkAction =>
        async (dispatch, getState) => {
            dispatch({
                type: "TOGGLE_SAVING_NEW_PARCEL",
                savingNewParcel: true,
                savingNewParcelErrors: []
            });

            const errors = await ParcelApi.CreateParcel(newParcel);
            dispatch({
                type: "TOGGLE_SAVING_NEW_PARCEL",
                savingNewParcel: false,
                savingNewParcelErrors: errors
            });

            if (errors.length === 0) {
                actionCreators.loadParcelList(url)(dispatch, getState);
            }
        },
    deleteParcel:
        (url: string, parcelId: string): IAppThunkAction =>
        async (dispatch, getState) => {
            dispatch({
                type: "TOGGLE_SAVING_PARCEL",
                parcelId,
                isSaving: true,
                savingErrors: []
            });

            const errors = await ParcelApi.DeleteParcel(parcelId);
            if (errors.length > 0) {
                dispatch({
                    type: "TOGGLE_SAVING_PARCEL",
                    parcelId,
                    isSaving: false,
                    savingErrors: errors
                });
            } else {
                actionCreators.loadParcelList(url)(dispatch, getState);
            }
        },
    loadParcelList:
        (url: string, loadBids = true): IAppThunkAction =>
        async (dispatch, getState) => {
            dispatch({
                type: "TOGGLE_PARCEL_LOAD_STATE",
                parcelsLoadState: LoadState.Loading
            });

            let auctionInfo = {} as IAuctionInfo;
            let parcels = [] as IParcelDetail[];
            const auctionParcels = await ParcelApi.GetParcels(url);

            if (auctionParcels) {
                auctionInfo = {
                    auctionId: auctionParcels.auctionId,
                    auctionLive: auctionParcels.auctionLive,
                    auctionName: auctionParcels.auctionName,
                    auctionStartDate: auctionParcels.auctionStartDate,
                    auctionUrl: auctionParcels.auctionUrl,
                    isApprovedAuctionBidder:
                        auctionParcels.isApprovedAuctionBidder
                } as IAuctionInfo;
                parcels = auctionParcels.parcels;
            }

            dispatch({
                type: "UPDATE_PARCEL_LIST",
                auctionInfo,
                parcels,
                parcelsLoadState: LoadState.Loaded
            });

            if (loadBids && auctionInfo.auctionId) {
                ParcelBidStore.actionCreators.loadParcelBidsForAuction(
                    auctionInfo.auctionId
                )(dispatch, getState);
            }
        },
    refreshParcelFileMetadata:
        (parcelId: string, fileType: FileType): IAppThunkAction =>
        async (dispatch, getState) => {
            const parcels = getState().parcelStoreState.parcels;
            const parcel = parcels.find((e) => e.parcelId === parcelId);
            if (!parcel) {
                return;
            }

            // choose dispatch based on file type
            if (fileType === FileType.ParcelCustom) {
                dispatch({
                    type: "TOGGLE_METADATA_CUSTOM_LOAD_STATE",
                    parcelId,
                    loadState: LoadState.Loading
                });

                const customMetadata = await FileApi.GetFileMetadata(
                    parcelId,
                    fileType
                );

                dispatch({
                    type: "UPDATE_METADATA_CUSTOM",
                    parcelId,
                    customMetadata
                });
            } else if (fileType === FileType.ParcelLandTitle) {
                dispatch({
                    type: "TOGGLE_METADATA_LAND_TITLE_LOAD_STATE",
                    parcelId,
                    loadState: LoadState.Loading
                });

                const landTitleMetadata = await FileApi.GetFileMetadata(
                    parcelId,
                    fileType
                );

                dispatch({
                    type: "UPDATE_METADATA_LAND_TITLE",
                    parcelId,
                    landTitleMetadata
                });
            } else if (fileType === FileType.ParcelPicture) {
                dispatch({
                    type: "TOGGLE_METADATA_PARCEL_PICTURE_LOAD_STATE",
                    parcelId,
                    loadState: LoadState.Loading
                });

                const pictureMetadata = await FileApi.GetFileMetadata(
                    parcelId,
                    fileType
                );
                const thumbMetadata = await FileApi.GetFileMetadata(
                    parcelId,
                    FileType.ParcelPictureThumbnail
                );

                dispatch({
                    type: "UPDATE_METADATA_PARCEL_PICTURE",
                    parcelId,
                    pictureMetadata,
                    thumbMetadata
                });
            } else if (fileType === FileType.ParcelPurchaseAgreement) {
                dispatch({
                    type: "TOGGLE_METADATA_PURC_AGMT_LOAD_STATE",
                    parcelId,
                    loadState: LoadState.Loading
                });

                const purchaseAgmtMetadata = await FileApi.GetFileMetadata(
                    parcelId,
                    fileType
                );

                dispatch({
                    type: "UPDATE_METADATA_PURC_AGMT",
                    parcelId,
                    purchaseAgmtMetadata
                });
            }
        },
    resetParcelSaveErrors:
        (parcelId: string): IAppThunkAction =>
        (dispatch) => {
            dispatch({
                type: "TOGGLE_SAVING_PARCEL",
                parcelId,
                isSaving: false,
                savingErrors: []
            });
        },
    toggleParcelMetadataLoadState:
        (
            parcelId: string,
            fileType: FileType,
            loadState: LoadState
        ): IAppThunkAction =>
        (dispatch) => {
            // choose dispatch based on file type
            if (fileType === FileType.ParcelCustom) {
                dispatch({
                    type: "TOGGLE_METADATA_CUSTOM_LOAD_STATE",
                    parcelId,
                    loadState
                });
            } else if (fileType === FileType.ParcelLandTitle) {
                dispatch({
                    type: "TOGGLE_METADATA_LAND_TITLE_LOAD_STATE",
                    parcelId,
                    loadState
                });
            } else if (fileType === FileType.ParcelPicture) {
                dispatch({
                    type: "TOGGLE_METADATA_PARCEL_PICTURE_LOAD_STATE",
                    parcelId,
                    loadState
                });
            } else if (fileType === FileType.ParcelPurchaseAgreement) {
                dispatch({
                    type: "TOGGLE_METADATA_PURC_AGMT_LOAD_STATE",
                    parcelId,
                    loadState
                });
            }
        },
    updateParcel:
        (
            parcelId: string,
            updatedParcel: ICreateUpdateParcel,
            bidders: string[],
            shouldUpdateBidders: boolean
        ): IAppThunkAction =>
        async (dispatch, getState) => {
            // generate patch for update
            const auctionInfo = getState().parcelStoreState.auctionInfo;
            const parcel = getState().parcelStoreState.parcels.find(
                (e) => e.parcelId === parcelId
            );
            if (!parcel || !auctionInfo) {
                return;
            }

            const oldParcel = {
                auctionEndDate: parcel.auctionEndDate,
                auctionId: auctionInfo.auctionId,
                auctionStartDate: parcel.auctionStartDate,
                bidIncrement: parcel.bidIncrement,
                enBloc: parcel.enBloc,
                isActive: parcel.isActive,
                isPrivateSale: parcel.isPrivateSale,
                lastAcceptedBid: parcel.lastAcceptedBid,
                hasAcceptedBids: parcel.hasAcceptedBids,
                latitude: parcel.latitude,
                longitude: parcel.longitude,
                name: parcel.name,
                lastAcceptedBidOverride: parcel.lastAcceptedBidOverride,
                slug: parcel.slug,
                startingBid: parcel.startingBid
            } as ICreateUpdateParcel;

            const patch = createPatch(oldParcel, updatedParcel);

            if (patch.length === 0 && !shouldUpdateBidders) {
                dispatch({
                    type: "TOGGLE_SAVING_PARCEL",
                    parcelId,
                    isSaving: true,
                    savingErrors: []
                });
                const errors = await ParcelApi.SyncSalesSite(
                    auctionInfo.auctionId
                );
                dispatch({
                    type: "TOGGLE_SAVING_PARCEL",
                    parcelId,
                    isSaving: false,
                    savingErrors: errors
                });

                return;
            }

            dispatch({
                type: "TOGGLE_SAVING_PARCEL",
                parcelId,
                isSaving: true,
                savingErrors: []
            });

            const errors = await ParcelApi.UpdateParcel(
                parcelId,
                patch,
                bidders
            );
            dispatch({
                type: "TOGGLE_SAVING_PARCEL",
                parcelId,
                isSaving: false,
                savingErrors: errors
            });

            if (errors.length === 0) {
                dispatch({ type: "UPDATE_PARCEL", parcelId, updatedParcel });
            }
        },
    archiveParcel:
        (parcelId: string, isArchiving: boolean): IAppThunkAction =>
        async (dispatch, getState) => {
            // generate patch for update
            const auctionInfo = getState().parcelStoreState.auctionInfo;
            const parcel = getState().parcelStoreState.parcels.find(
                (e) => e.parcelId === parcelId
            );
            if (!parcel || !auctionInfo) {
                return;
            }

            dispatch({
                type: "TOGGLE_SAVING_PARCEL",
                parcelId,
                isSaving: true,
                savingErrors: []
            });

            const errors = await ParcelApi.ArchiveParcel(parcelId, isArchiving);
            dispatch({
                type: "TOGGLE_SAVING_PARCEL",
                parcelId,
                isSaving: false,
                savingErrors: errors
            });

            if (errors.length === 0) {
                dispatch({ type: "ARCHIVE_PARCEL", parcelId, isArchiving });
            }
        },
    updateParcelOrder:
        (sortParcel: ISortParcel[]): IAppThunkAction =>
        async (dispatch) => {
            dispatch({
                type: "TOGGLE_UPDATING_PARCEL_ORDER",
                isSaving: true,
                updatingErrors: []
            });

            const result = await ParcelApi.UpdateParcelOrder(sortParcel);
            dispatch({
                type: "TOGGLE_UPDATING_PARCEL_ORDER",
                isSaving: false,
                updatingErrors: result.errors
            });

            if (result.parcels) {
                const auctionParcels = result.parcels;

                const auctionInfo = {
                    auctionId: auctionParcels.auctionId,
                    auctionLive: auctionParcels.auctionLive,
                    auctionName: auctionParcels.auctionName,
                    auctionStartDate: auctionParcels.auctionStartDate,
                    auctionUrl: auctionParcels.auctionUrl,
                    isApprovedAuctionBidder:
                        auctionParcels.isApprovedAuctionBidder
                } as IAuctionInfo;
                const parcels = auctionParcels.parcels;

                dispatch({
                    type: "UPDATE_PARCEL_LIST",
                    auctionInfo,
                    parcels,
                    parcelsLoadState: LoadState.Loaded
                });
            }
        }
};

export const reducer: Reducer<IParcelStoreState> = (
    state: IParcelStoreState | undefined,
    incomingAction: AnyAction
) => {
    const action = incomingAction as KnownAction;
    const prevState = state || defaultState;

    switch (action.type) {
        case "ARCHIVE_PARCEL": {
            const parcels = prevState.parcels.map((e) => {
                if (e.parcelId !== action.parcelId) {
                    return e;
                }

                return {
                    ...e,
                    isActive: !action.isArchiving
                } as IParcelDetail;
            });

            return {
                ...prevState,
                parcels
            };
        }
        case "DELETE_PARCEL": {
            const parcels = prevState.parcels.filter(
                (e) => e.parcelId !== action.parcelId
            );

            return {
                ...prevState,
                parcels
            };
        }
        case "HIDE_PARCEL_UPDATED_SNACKBAR_MESSAGE": {
            return {
                ...prevState,
                parcelUpdateMessage: "",
                showParcelUpdateMessage: false
            };
        }
        case "SHOW_PARCEL_UPDATED_SNACKBAR_MESSAGE": {
            return {
                ...prevState,
                parcelUpdateMessage: action.message,
                showParcelUpdateMessage:
                    prevState.auctionInfo.auctionId === action.auctionId
            };
        }
        case "TOGGLE_AUCTION_LIVE_STATUS": {
            const auctionInfo = { ...prevState.auctionInfo };
            if (auctionInfo.auctionId === action.auctionId) {
                auctionInfo.auctionLive = action.live;
            }

            return {
                ...prevState,
                auctionInfo
            };
        }

        case "TOGGLE_METADATA_CUSTOM_LOAD_STATE": {
            const parcels = prevState.parcels.map((e) => {
                if (e.parcelId !== action.parcelId) {
                    return e;
                }

                return {
                    ...e,
                    customMetadataLoadState: action.loadState
                } as IParcelDetail;
            });

            return {
                ...prevState,
                parcels
            };
        }
        case "TOGGLE_METADATA_LAND_TITLE_LOAD_STATE": {
            const parcels = prevState.parcels.map((e) => {
                if (e.parcelId !== action.parcelId) {
                    return e;
                }

                return {
                    ...e,
                    landTitleMetadataLoadState: action.loadState
                } as IParcelDetail;
            });

            return {
                ...prevState,
                parcels
            };
        }
        case "TOGGLE_METADATA_PARCEL_PICTURE_LOAD_STATE": {
            const parcels = prevState.parcels.map((e) => {
                if (e.parcelId !== action.parcelId) {
                    return e;
                }

                return {
                    ...e,
                    pictureMetadataLoadState: action.loadState
                } as IParcelDetail;
            });

            return {
                ...prevState,
                parcels
            };
        }
        case "TOGGLE_METADATA_PURC_AGMT_LOAD_STATE": {
            const parcels = prevState.parcels.map((e) => {
                if (e.parcelId !== action.parcelId) {
                    return e;
                }

                return {
                    ...e,
                    purcAgmtMetadataLoadState: action.loadState
                } as IParcelDetail;
            });

            return {
                ...prevState,
                parcels
            };
        }
        case "TOGGLE_PARCEL_LIVE_STATUS": {
            const parcels = prevState.parcels.map((e) => {
                if (e.parcelId !== action.parcelId) {
                    return e;
                }

                return {
                    ...e,
                    auctionEndDate: action.endDate,
                    auctionStartDate: action.startDate,
                    isCompleted: action.isCompleted,
                    isLive: action.isLive
                } as IParcelDetail;
            });

            return {
                ...prevState,
                parcels
            };
        }
        case "TOGGLE_PARCEL_LOAD_STATE": {
            return {
                ...prevState,
                parcelsLoadState: action.parcelsLoadState
            };
        }
        case "TOGGLE_SAVING_NEW_PARCEL": {
            return {
                ...prevState,
                savingNewParcel: action.savingNewParcel,
                savingNewParcelErrors: action.savingNewParcelErrors
            };
        }
        case "TOGGLE_SAVING_PARCEL": {
            const parcels = prevState.parcels.map((e) => {
                if (e.parcelId !== action.parcelId) {
                    return e;
                }

                return {
                    ...e,
                    isSaving: action.isSaving,
                    savingErrors: action.savingErrors
                } as IParcelDetail;
            });

            return {
                ...prevState,
                parcels
            };
        }
        case "TOGGLE_UPDATING_PARCEL_ORDER": {
            return {
                ...prevState,
                savingParcelOrder: action.isSaving,
                savingParcelOrderErrors: action.updatingErrors
            };
        }
        case "UPDATE_METADATA_CUSTOM": {
            const parcels = prevState.parcels.map((e) => {
                if (e.parcelId !== action.parcelId) {
                    return e;
                }

                return {
                    ...e,
                    customMetadata: action.customMetadata,
                    customMetadataLoadState: LoadState.Loaded
                } as IParcelDetail;
            });

            return {
                ...prevState,
                parcels
            };
        }
        case "UPDATE_METADATA_LAND_TITLE": {
            const parcels = prevState.parcels.map((e) => {
                if (e.parcelId !== action.parcelId) {
                    return e;
                }

                return {
                    ...e,
                    landTitleMetadata: action.landTitleMetadata,
                    landTitleMetadataLoadState: LoadState.Loaded
                } as IParcelDetail;
            });

            return {
                ...prevState,
                parcels
            };
        }
        case "UPDATE_METADATA_PARCEL_PICTURE": {
            const parcels = prevState.parcels.map((e) => {
                if (e.parcelId !== action.parcelId) {
                    return e;
                }

                return {
                    ...e,
                    pictureMetadata: action.pictureMetadata,
                    pictureMetadataLoadState: LoadState.Loaded,
                    thumbnailMetadata: action.thumbMetadata
                } as IParcelDetail;
            });

            return {
                ...prevState,
                parcels
            };
        }
        case "UPDATE_METADATA_PURC_AGMT": {
            const parcels = prevState.parcels.map((e) => {
                if (e.parcelId !== action.parcelId) {
                    return e;
                }

                return {
                    ...e,
                    purcAgmtMetadataLoadState: LoadState.Loaded,
                    purchaseAgreementMetadata:
                        action.purchaseAgmtMetadata.length > 0
                            ? action.purchaseAgmtMetadata[0]
                            : null
                } as IParcelDetail;
            });

            return {
                ...prevState,
                parcels
            };
        }
        case "UPDATE_PARCEL": {
            const parcels = prevState.parcels.map((e) => {
                if (e.parcelId !== action.parcelId) {
                    return e;
                }

                return {
                    ...e,
                    auctionEndDate: action.updatedParcel.auctionEndDate,
                    auctionStartDate: action.updatedParcel.auctionStartDate,
                    bidIncrement: action.updatedParcel.bidIncrement,
                    enBloc: action.updatedParcel.enBloc,
                    isPrivateSale: action.updatedParcel.isPrivateSale,
                    lastAcceptedBid: action.updatedParcel.isPrivateSale
                        ? action.updatedParcel.lastAcceptedBid
                        : action.updatedParcel.startingBid,
                    hasAcceptedBids: action.updatedParcel.hasAcceptedBids,
                    lastAcceptedBidOverride:
                        action.updatedParcel.lastAcceptedBidOverride,
                    latitude: action.updatedParcel.latitude,
                    longitude: action.updatedParcel.longitude,
                    name: action.updatedParcel.name,
                    slug: action.updatedParcel.slug,
                    startingBid: action.updatedParcel.startingBid
                } as IParcelDetail;
            });

            return {
                ...prevState,
                parcels
            };
        }
        case "UPDATE_LIVE_PARCEL_INFORMATION": {
            const parcels = prevState.parcels.map((e) => {
                if (e.parcelId !== action.auctionStatus.parcelId) {
                    return e;
                }
                const auctionStatusBids =
                    action.auctionStatus.bids &&
                    action.auctionStatus.bids[action.auctionStatus.parcelId]
                        ? action.auctionStatus.bids[
                              action.auctionStatus.parcelId
                          ]
                        : [];
                const latestStatusBidAmount =
                    auctionStatusBids && auctionStatusBids.length > 0
                        ? auctionStatusBids[auctionStatusBids.length - 1]
                              .bidAmount
                        : null;
                const latestBidAmount =
                    latestStatusBidAmount &&
                    e.lastAcceptedBid !== latestStatusBidAmount
                        ? latestStatusBidAmount
                        : e.lastAcceptedBid;

                const startDate = action.auctionStatus.parcelStartDate
                    ? action.auctionStatus.parcelStartDate
                    : e.auctionStartDate;
                const endDate = action.auctionStatus.parcelEndDate
                    ? action.auctionStatus.parcelEndDate
                    : e.auctionEndDate;

                const isCompleted = action.auctionStatus.status
                    ? action.auctionStatus.status === "Completed"
                    : e.isCompleted;
                const isLive = action.auctionStatus.status
                    ? action.auctionStatus.status === "Live"
                    : e.isLive;
                const hasAcceptedBids =
                    action.auctionStatus.bids[action.auctionStatus.parcelId] !=
                    null
                        ? action.auctionStatus.bids[
                              action.auctionStatus.parcelId
                          ].length > 0
                        : false;

                return {
                    ...e,
                    auctionEndDate: endDate,
                    auctionStartDate: startDate,
                    checkOutToken: action.auctionStatus.checkOutToken,
                    previousCheckoutToken: action.auctionStatus.checkOutToken,
                    isActive: action.auctionStatus.isActive,
                    isCompleted,
                    isLive,
                    lastAcceptedBid: latestBidAmount,
                    approvedEnBlocBidders:
                        action.auctionStatus.approvedEnBlocBidders || [],
                    hasAcceptedBids
                } as IParcelDetail;
            });

            return {
                ...prevState,
                parcels
            };
        }
        case "UPDATE_PARCEL_BID_AMOUNT": {
            const parcels = prevState.parcels.map((e) => {
                if (e.parcelId !== action.parcelId) {
                    return e;
                }

                return {
                    ...e,
                    checkOutToken: action.tokenId,
                    previousCheckoutToken: action.tokenId,
                    lastAcceptedBid: action.amount,
                    auctionEndDate: action.endDate,
                    hasAcceptedBids: true
                } as IParcelDetail;
            });

            return {
                ...prevState,
                parcels
            };
        }
        case "UPDATE_PARCEL_LIST": {
            return {
                ...prevState,
                auctionInfo: action.auctionInfo,
                parcels: action.parcels,
                parcelsLoadState: action.parcelsLoadState
            };
        }
        case "UPDATE_PARCEL_BIDDERS": {
            return {
                ...prevState,
                parcels: prevState.parcels.map((p) => {
                    if (p.parcelId !== action.parcelId) {
                        return p;
                    }
                    return {
                        ...p,
                        approvedEnBlocBidders: action.approvedEnBlocBidders
                    };
                })
            };
        }
        case "CHECKPOINT_LIVE_PARCELS": {
            // If we receive messages from the hub that do not include `liveParcels`,
            //   these messages are from a deprecated version of the app.
            if (!action.liveParcels) {
                return prevState;
            }

            const nextParcels = prevState.parcels.map((prevParcel) => {
                const parcelUpdate = action.liveParcels.find(
                    (nextParcel) => nextParcel.parcelId === prevParcel.parcelId
                );

                // Auction has closed
                if (!parcelUpdate) {
                    return {
                        ...prevParcel,
                        isLive: false
                    };
                }

                if (prevParcel.previousCheckoutToken === undefined) {
                    prevParcel.previousCheckoutToken =
                        parcelUpdate.checkOutToken;
                } else if (
                    prevParcel.checkOutToken === parcelUpdate.checkOutToken &&
                    prevParcel.checkOutToken !==
                        prevParcel.previousCheckoutToken
                ) {
                    // The checkout tokens have been out of sync for 2 heartbeats, reset the SignalR connection
                    action.resetConnection();
                }

                return {
                    ...prevParcel,
                    auctionStartDate: parcelUpdate.parcelStartDate,
                    auctionEndDate: parcelUpdate.parcelEndDate,
                    checkOutToken: parcelUpdate.checkOutToken,
                    previousCheckoutToken: parcelUpdate.checkOutToken,
                    isLive: parcelUpdate.status === AuctionStatus.Live
                };
            });

            return {
                ...prevState,
                parcels: nextParcels
            };
        }
        default: {
            break;
        }
    }

    return prevState;
};

export const defaultState = {
    auctionInfo: {} as IAuctionInfo,
    parcelUpdateMessage: "",
    parcels: [],
    parcelsLoadState: LoadState.NotLoaded,
    savingNewParcel: false,
    savingNewParcelErrors: [],
    savingParcelOrder: false,
    savingParcelOrderErrors: [],
    showParcelUpdateMessage: false
} as IParcelStoreState;
